import React, { FC, useEffect, useMemo, useState } from 'react';
import { Box, Button, Container, Divider, Grid, LinearProgress, TextField, Typography } from '@material-ui/core';
import Card from 'src/components/Card';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import MomentUtils from '@date-io/moment';
import { MuiPickersUtilsProvider } from '@material-ui/pickers';
import SignatureField from 'src/scenes/components/SignatureField';
import DatePicker from 'src/components/DatePicker';
import TimePicker from 'src/components/TimePicker';
import InterventionOrder, { PostInterventionOrderBody } from 'src/interfaces/api/InterventionOrder';
import moment from 'moment';
import AskBeforeLeaving from 'src/components/AskBeforeLeaving';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import Badge from 'src/components/Badge';
import PhotoPicker from '../../../components/PhotoPicker';
import api from 'src/config/api';
import YesNoField from 'src/scenes/InterventionOrder/scenes/View/components/InstallationSection/components/YesNoField';
import InstallationSection from 'src/scenes/InterventionOrder/scenes/View/components/InstallationSection';
import InformationSection from 'src/scenes/InterventionOrder/scenes/View/components/InformationSection';
import Site from 'src/interfaces/api/Site';

interface Props {
  onSaveInterventionOrder: (interventionOrder: PostInterventionOrderBody) => Promise<InterventionOrder>;
  onSuccess: () => void;
  interventionOrder?: InterventionOrder;
  site: Partial<Site>;
  ticketId: string;
  isTicketTelecom?: boolean;
  contact: string;
}

const InterventionOrder: FC<Props> = ({
  isTicketTelecom,
  ticketId,
  site,
  contact,
  interventionOrder,
  onSaveInterventionOrder,
  onSuccess,
}) => {
  const [isSendConfirmationDialogOpened, setIsSendConfirmationDialogOpened] = useState(false);
  const [formError, setFormError] = useState<string>();

  const defaultValues: PostInterventionOrderBody = useMemo(
    () => ({
      hasContract: false,
      toBilled: false,
      date: moment().format(),
      startTime: moment().format('HH:mm'),
      endTime: moment().add(1, 'minute').format('HH:mm'),
      ticketId,
      socialReason: '',
      address: '',
      postalCode: '',
      city: '',
      price: 0,
      object: '',
      observations: '',
      additionalInformations: '',
      contactName: '',
      signature: '',
      workFinished: '',
      hasPicture: '',
      upstreamRate: '',
      downstreamRate: '',
      callTest: '',
      callLocationTest: '',
      scannerTest: '',
      faxTest: '',
      printerTest: '',
      tpeTest: '',
      portabilityTest: '',
      alarmTest: '',
      frankingMachineTest: '',
      mailTest: '',
      recipientEmails: '',
      attachments: [],
      toSend: false,
    }),
    []
  );

  const formMethods = useForm<PostInterventionOrderBody>({ defaultValues });

  useEffect(() => {
    formMethods.setValue('socialReason', site.socialReason || '');
    formMethods.setValue('address', site.address || '');
    formMethods.setValue('postalCode', site.postalCode || '');
    formMethods.setValue('city', site.city || '');
  }, [site]);

  useEffect(() => {
    formMethods.setValue('contactName', contact, { shouldValidate: true });
  }, [contact]);

  const handleSubmit = () => {
    setIsSendConfirmationDialogOpened(false);
    formMethods.handleSubmit((data) => onSubmit({ ...data, toSend: true }))();
  };

  const onSubmit = async (interventionOrder: PostInterventionOrderBody) => {
    try {
      await onSaveInterventionOrder(interventionOrder);
      setFormError(undefined);
      onSuccess();
    } catch (e) {
      setFormError(e.message);
    }
  };

  const watchStartTime = formMethods.watch('startTime');

  return (
    <MuiPickersUtilsProvider utils={MomentUtils}>
      <AskBeforeLeaving
        message="Le formulaire n'a pas été enregistré, êtes-vous sûr de vouloir partir ?"
        condition={formMethods.formState.isDirty && !formMethods.formState.isSubmitting}
      />
      <Box mt='50px' mb='50px' display='flex' alignItems='center' clone>
        <Container maxWidth='md'>
          <FormProvider {...formMethods}>
            <Card>
              <Box mb='24px !important' clone>
                <Typography variant='h5'>Bon d'intervention</Typography>
              </Box>

              <Grid container spacing={2}>
                <Grid item xs={12} sm={4}>
                  <Controller
                    name='date'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <DatePicker
                        {...field}
                        label='Date'
                        format='DD/MM/YYYY'
                        inputVariant='outlined'
                        helperText={error?.message}
                        error={undefined !== error}
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Controller
                    name='startTime'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                      <TimePicker
                        {...field}
                        ampm={false}
                        value={value ? moment(value, 'HH:mm') : null}
                        onChange={(value) => onChange(value?.format('HH:mm'))}
                        label="Heure d'arrivée"
                        inputVariant='outlined'
                        helperText={error?.message}
                        error={undefined !== error}
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={6} sm={4}>
                  <Controller
                    name='endTime'
                    control={formMethods.control}
                    rules={{
                      required: { value: true, message: 'Le champ est requis' },
                      validate: (value) =>
                        moment(value, 'HH:mm').isAfter(moment(watchStartTime, 'HH:mm'), 'minute') ||
                        "L'heure de départ doit être après l'heure d'arrivée",
                    }}
                    render={({ field: { onChange, value, ...field }, fieldState: { error } }) => (
                      <TimePicker
                        {...field}
                        ampm={false}
                        label='Heure de départ'
                        inputVariant='outlined'
                        value={value ? moment(value, 'HH:mm') : null}
                        onChange={(value) => onChange(value?.format('HH:mm'))}
                        helperText={error?.message}
                        error={undefined !== error}
                        required
                        fullWidth
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Box color='#637381' clone>
                    <Typography variant='overline'>Informations</Typography>
                  </Box>
                </Grid>
                <Grid item xs={12}>
                  <InformationSection isSiteReadOnly />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='object'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Objet de l'intervention"
                        variant='outlined'
                        multiline
                        minRows={2}
                        fullWidth
                        required
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='observations'
                    control={formMethods.control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label='Observations'
                        variant='outlined'
                        multiline
                        minRows={4}
                        fullWidth
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                {isTicketTelecom && (
                  <>
                    <Grid item xs={12}>
                      <Box color='#637381' clone>
                        <Typography variant='overline'>Installation</Typography>
                      </Box>
                    </Grid>

                    <Grid item xs={12}>
                      <InstallationSection />
                    </Grid>
                  </>
                )}
                <Grid item xs={12}>
                  <Divider component='div' />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='additionalInformations'
                    control={formMethods.control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label="Compléments d'informations"
                        variant='outlined'
                        multiline
                        minRows={2}
                        fullWidth
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='workFinished'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <YesNoField
                        {...field}
                        error={undefined !== error}
                        helperText={error?.message}
                        label='Travail terminé ?'
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='hasPicture'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <YesNoField
                        {...field}
                        error={undefined !== error}
                        helperText={error?.message}
                        label='Photo avant / après'
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='attachments'
                    control={formMethods.control}
                    render={({ field: { ref, ...otherField }, fieldState: { error } }) => (
                      <PhotoPicker
                        {...otherField}
                        error={undefined !== error}
                        helperText={error?.message}
                        itemId={interventionOrder?.id}
                        getItemAttachmentApi={(attachmentId: string) =>
                          api.interventionOrder.getAttachment(interventionOrder!.id, attachmentId)
                        }
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  <Controller
                    name='contactName'
                    control={formMethods.control}
                    rules={{ required: { value: true, message: 'Le champ est requis' } }}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label='Nom du contact'
                        variant='outlined'
                        fullWidth
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>
                <Grid item xs={12}>
                  {interventionOrder?.signatureId ? (
                    <Badge color='primary' text='Signature déjà enregistré !' />
                  ) : (
                    <SignatureField
                      mainText='Le client reconnaît que son installation est en bon état de fonctionnement après intervention du technicien.'
                      footerText={
                        <>
                          Chaque intervention facturable comporte au minimum un forfait de déplacement et une heure
                          d’intervention au tarif en vigueur.
                          <br />
                          Chaque heure entamée est due. Le client reconnaît avoir pris connaisance des tarifs des
                          pièces, celle-ci étant obligatoirement facturée en sus dans le cadre de la maintenance ou de
                          dépannage n’incluant pas ces pièces. Clause de réserve de propriété (loi 12.05.80) : la
                          marchandise et les fournitures resteront notre propriété jusqu’au jour de leur règlement
                          intégral.
                          <br />
                          Seul le tribunal du siège du siège social de la société intervenante est compétent en cas de
                          recours.
                        </>
                      }
                    />
                  )}
                </Grid>
                <Box display='flex' flexDirection='column' clone>
                  <Grid item xs={12}>
                    <Box color='#637381' clone>
                      <Typography variant='overline'>Envoyez le bon d'intervention aux adresses suivantes</Typography>
                    </Box>

                    <Box color='#637381' clone>
                      <Typography variant='caption'>Séparez les mails par des virgules</Typography>
                    </Box>
                  </Grid>
                </Box>
                <Grid item xs={12}>
                  <Controller
                    name='recipientEmails'
                    control={formMethods.control}
                    render={({ field, fieldState: { error } }) => (
                      <TextField
                        {...field}
                        label='Adresses mails'
                        variant='outlined'
                        fullWidth
                        error={undefined !== error}
                        helperText={error?.message}
                      />
                    )}
                  />
                </Grid>

                {formError && (
                  <Grid item xs={12}>
                    <Box component='p' py='25px' fontSize='.75rem' color='#f44336'>
                      {formError}
                    </Box>
                  </Grid>
                )}
                {!interventionOrder?.sended && (
                  <Grid item xs={12}>
                    <Grid container justifyContent='flex-end'>
                      <Grid item xs={4}>
                        <Button
                          fullWidth
                          color='primary'
                          variant='contained'
                          disabled={formMethods.formState.isSubmitting}
                          onClick={() => setIsSendConfirmationDialogOpened(true)}
                        >
                          Envoyer
                        </Button>
                      </Grid>
                    </Grid>
                  </Grid>
                )}
              </Grid>

              {formMethods.formState.isSubmitting && (
                <Box mt='5px'>
                  <LinearProgress />
                </Box>
              )}
            </Card>
          </FormProvider>
        </Container>
      </Box>

      <ConfirmationDialog
        open={isSendConfirmationDialogOpened}
        title='Attention'
        description={`Vous êtes sur le point d'envoyer le bon d'intervention.\nIl sera ajouté sur le mur du client puis envoyé aux adresses que vous avez défini.\nUne fois envoyé, il vous sera impossible de l'editer.`}
        confirmationText='Valider'
        onValidate={handleSubmit}
        onClose={() => setIsSendConfirmationDialogOpened(false)}
      />
    </MuiPickersUtilsProvider>
  );
};

export default InterventionOrder;
